import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import style from "./style.module.scss";
import { BtnLink } from "../BtnLink";
import appConfig from "../../util/appConfig";
import { useAppDispatch, useAppSelector } from "../../store";
import awlLogo from "../../assets/images/logos/awlLogoNew.png";
import bkLogo from "../../assets/images/logos/bkLogo.svg";
import sensisLogo from "../../assets/images/logos/sensisLogo.svg";
import ebwoLogo from "../../assets/images/logos/ebwoLogo.jpg";
import lraLogo from "../../assets/images/logos/lraLogo.svg";
import abvLogo from "../../assets/images/logos/abvLogo.svg";
import lraMobileLogo from "../../assets/images/logos/lraMobileLogo.svg";
import calwLogo from "../../assets/images/logos/calwLogo.svg";
import { setIsBurgerMenu } from "../../store/slices/app";
import { HeaderSelect } from "../HeaderSelect";
import { SmallLoader } from "../Loader";
import { userToken } from "../../util/userToken";

export const Header = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading, isBurgerMenuOpen, isMobile } = useAppSelector(
    (state) => state.app.client
  );
  const {
    objectIdUser: { nachname },
    isUserDataLoading,
  } = useAppSelector((state) => state.user);

  const handleBurgerMenu = () => {
    dispatch(setIsBurgerMenu(!isBurgerMenuOpen));
  };

  const logo = (logo: string) => {
    switch (logo) {
      case "awl": {
        return awlLogo;
      }
      case "bk": {
        return bkLogo;
      }
      case "sensis": {
        return sensisLogo;
      }
      case "ebwo": {
        return ebwoLogo;
      }
      case "lra": {
        return isMobile ? lraMobileLogo : lraLogo;
      }
      case "calw": {
        return calwLogo;
      }
      case "viersen": {
        return abvLogo;
      }
      default: {
        return sensisLogo;
      }
    }
  };

  useEffect(() => {
    const header = document.querySelector<HTMLElement>("#header");
    const platform = window.navigator.platform;
    if (isLoading || isBurgerMenuOpen) {
      document.body.style.overflow = "hidden";
      if (header && platform.includes("Win")) {
        header.style.paddingRight = "17px";
      }
    }

    if (!isLoading && !isBurgerMenuOpen) {
      document.body.style.overflow = "inherit";
      if (header && platform.includes("Win")) {
        header.style.paddingRight = "0";
      }
    }
  }, [isBurgerMenuOpen, isLoading]);

  if (["viersen"].includes(appConfig.logo) && location.pathname === "/login") {
    return null;
  }

  const generateLink = (type: string) => {
    switch (type) {
      case "viersen": {
        return "/sepa-management";
      }
      default: {
        return "/";
      }
    }
  };

  const generateLogoAltText = (env: string) => {
    switch (env) {
      case "lra": {
        return "Logo des Landratsamts Aichach-Friedberg";
      }
      case "awl": {
        return "Logo der AWL Neuss";
      }
      default: {
        return env;
      }
    }
  };

  return (
    <header
      className={style.header}
      id="header"
      style={{
        filter: isLoading ? "blur(2px)" : "",
      }}
    >
      <div className={style.header_container}>
        <nav className={style.nav_container}>
          <div>
            <Link
              to={generateLink(appConfig.logo)}
              onClick={() =>
                window.scrollTo({
                  top: 0,
                })
              }
            >
              <img
                src={logo(appConfig.logo)}
                alt={generateLogoAltText(appConfig.logo)}
                style={{
                  maxWidth: appConfig.logo === "awl" ? "74px" : "",
                }}
                className={appConfig.logo === "ebwo" ? style.ebwoLogo : ""}
              />
            </Link>
          </div>
          {appConfig.logo === "sensis" && (
            <div className={style.auth_links_container}>
              <div className={style.login_link}>
                <a href={appConfig.nrwLoginURl}>Anmelden</a>
              </div>
              <BtnLink
                isHashLink={true}
                link={appConfig.nrwSignupUrl}
                title="Registrieren"
              />
            </div>
          )}
          {["lra", "calw"].includes(appConfig.logo) && !isMobile ? (
            isUserDataLoading ? (
              <SmallLoader loaderDotSize="7" />
            ) : !nachname ? (
              <Link
                to={"/login"}
                className={`${style.portal_link} ${style.main_link} ${style.anmelden}`}
              >
                Anmelden
              </Link>
            ) : (
              <HeaderSelect />
            )
          ) : null}
          {["viersen"].includes(appConfig.logo) ? (
            <div
              onClick={() => {
                userToken.remove();
                navigate("/login");
              }}
              style={{
                fontFamily: "Dosis",
                cursor: "pointer",
              }}
            >
              Ausloggen
            </div>
          ) : null}
          {["viersen", "calw"].includes(appConfig.logo) ? null : (
            <div
              className={
                ["sensis", "lra"].includes(appConfig.logo)
                  ? style.nav_absolute_container
                  : style.nav_absolute_container2
              }
              style={{
                gap: "60px",
              }}
            >
              {!["awl", "lra"].includes(appConfig.logo) ? (
                <div
                  className={`${style.portal_link} ${style.main_link}`}
                  style={{
                    marginRight: 0,
                  }}
                >
                  <Link to="/">PORTAL</Link>
                </div>
              ) : null}
              {!["lra"].includes(appConfig.logo) ? (
                <div
                  className={`${style.main_link} ${
                    appConfig.logo === "sensis" ? style.faq_link : ""
                  }`}
                >
                  <a href="/#faq">FAQS</a>
                </div>
              ) : null}
              {appConfig.logo === "sensis" ? (
                <div className={`${style.main_link}`}>
                  <Link to="/actual">AKTUELLES</Link>
                </div>
              ) : null}
            </div>
          )}
        </nav>

        {isMobile && !["viersen"].includes(appConfig.logo) ? (
          <div
            className={style.burgerContainer}
            onClick={handleBurgerMenu}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleBurgerMenu();
              }
            }}
          >
            <div className={style.burgerWrapper}>
              <div
                className={`${style.middle} ${
                  isBurgerMenuOpen ? style.isMiddleOpened : ""
                }`}
              ></div>
              <div
                className={`${style.long} ${
                  isBurgerMenuOpen ? style.isLongOpened : ""
                }`}
              ></div>
              <div
                className={`${style.short} ${
                  isBurgerMenuOpen ? style.isShortOpened : ""
                }`}
              ></div>
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
};
